/**
 * ExtractedTextDialog — restores the complete extracted source text before
 * displaying citation offsets. A site-record preview is never labeled complete.
 *
 * Two roles:
 *   - Fallback path when a type-specific viewer fails (the text is
 *     independent of the lazy viewer chunk, so it always works).
 *   - A primary "show me what the AI sees" affordance (header button
 *     in DocViewerDialog; can be surfaced elsewhere too).
 *
 * The body loader shares the authenticated source cache. Offset highlights
 * use the complete captured text without changing persisted document metadata.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { X, Copy, Check, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { SiteDocument } from '../../../types/siteRag';
import type { SedSectionId } from '../../../types/siteEvidence';
import { SED_SECTION_LABELS } from '../../../lib/sedStaleness';
import { HighlightedText } from './HighlightedText';
import { documentBodyText, documentNeedsBodyHydration, hydrateDocumentBodiesForEvidence, type DocumentBodyLoader } from '../../../lib/documentBody';

interface Props {
  bodyLoader?: DocumentBodyLoader;
  doc: SiteDocument | null;
  open: boolean;
  onClose: () => void;
  /**
   * SET-D5 — SED-ingestion transparency block. When provided, renders
   * a footer panel below the extracted text with the SED sections this
   * doc contributes to, the KSD6 contentHash cache key, and a
   * Re-extract action. Optional so callers without SED knowledge
   * (legacy ErrorBoundary fallback, report-mode DocumentLibrary) drop
   * the panel and keep the dialog unchanged.
   */
  ingestionContributedSections?: SedSectionId[];
  /** SET-D5 — handler for the Re-extract action. KSD5
   *  `reExtractDocForSed` is the canonical implementation; pass it in
   *  from the SiteEvidenceTab. Omit to hide the action button. */
  onReExtract?: (doc: SiteDocument) => void | Promise<void>;
  /**
   * H-4 (passage-offset citation deep-links) — when supplied, the
   * extracted text renders with this `[start, end)` char span highlighted
   * (a `<mark>` auto-scrolled into view) instead of the plain `<pre>`.
   * Plumbed by `DocViewerDialog` from a `[Ref N]` whose
   * `LinkedDocumentRef.sourceOffset` matched a verbatim excerpt. Omit
   * (every other caller) and the panel renders the plain extracted text
   * exactly as before.
   */
  highlightOffset?: { start: number; end: number };
}

export const ExtractedTextDialog: React.FC<Props> = ({
  doc,
  open,
  onClose,
  ingestionContributedSections,
  onReExtract,
  highlightOffset,
  bodyLoader,
}) => {
  const [copied, setCopied] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [bodyRead, setBodyRead] = React.useState<{ source: SiteDocument; document?: SiteDocument; error?: string } | null>(null);
  React.useEffect(() => {
    if (!open || !doc || !documentNeedsBodyHydration(doc)) return;
    let cancelled = false;
    void hydrateDocumentBodiesForEvidence([doc], { loader: bodyLoader }).then(([document]) => {
      if (!cancelled && document) setBodyRead({ source: doc, document });
    }).catch((error: unknown) => {
      if (!cancelled) setBodyRead({ source: doc, error: error instanceof Error ? error.message : 'Full document text is unavailable.' });
    });
    return () => { cancelled = true; };
  }, [open, doc, bodyLoader]);
  // Reset the copied affordance whenever the dialog closes / the doc changes.
  React.useEffect(() => {
    if (!open) {
      setCopied(false);
      setIsFullscreen(false);
    }
  }, [open]);

  if (!doc) return null;
  // H-4 — the `sourceOffset` char span was computed against the RAW
  // `textContent` (not trimmed), so when a highlight offset is supplied we
  // must render the raw text for the indices to line up. With no offset we
  // keep trimming, byte-identical to the prior behaviour.
  const loaded = bodyRead?.source === doc ? bodyRead : null;
  const sourceDocument = loaded?.document ?? doc;
  const incomplete = documentNeedsBodyHydration(sourceDocument);
  const rawText = documentBodyText(sourceDocument);
  const text = highlightOffset ? rawText : rawText.trim();
  const hasText = text.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent
        className={cn(
          'flex flex-col p-0 gap-0 w-[calc(100vw-2rem)]',
          isFullscreen
            ? 'h-[calc(100vh-1rem)] max-w-none max-h-none'
            : 'max-w-[min(90vw,900px)] h-[min(80vh,700px)]',
        )}
        data-testid="extracted-text-dialog"
        data-fullscreen={isFullscreen ? 'true' : 'false'}
      >
        <DialogHeader className="px-4 py-3 border-b border-border flex flex-row items-center justify-between">
          <DialogTitle className="text-sm font-bold truncate flex-1 min-w-0 mr-3">
            Extracted text — {doc.fileName}
          </DialogTitle>
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm" variant="ghost" className="text-xs"
              title={isFullscreen ? 'Exit full screen' : 'Full screen'}
              aria-pressed={isFullscreen}
              onClick={() => setIsFullscreen(v => !v)}
              data-testid="extracted-text-fullscreen"
            >
              {isFullscreen
                ? <Minimize2 className="w-4 h-4 mr-1" aria-hidden />
                : <Maximize2 className="w-4 h-4 mr-1" aria-hidden />}
              {isFullscreen ? 'Exit' : 'Full screen'}
            </Button>
            <Button
              size="sm" variant="outline" className="text-xs"
              disabled={!hasText || incomplete}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(text);
                  setCopied(true);
                  toast.success('Extracted text copied');
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  toast.error('Could not copy to clipboard');
                }
              }}
              data-testid="extracted-text-copy"
            >
              {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} title="Close" data-testid="extracted-text-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="px-4 py-2 border-b border-border bg-muted/40 text-[10px] text-text-muted uppercase tracking-widest">
          Extracted source text used by evidence readers and document retrieval.
        </div>
        {incomplete && <p role="status" className="px-4 py-2 text-xs text-text-secondary" data-testid="extracted-text-body-status">
          {loaded?.error ? `Preview only. ${loaded.error}` : 'Loading the full source text… The text below is a preview.'}
        </p>}
        <div className="flex-1 min-h-0 overflow-auto bg-background">
          {hasText ? (
            <HighlightedText
              text={text}
              // H-4 — highlight the cited passage when a deep-link offset
              // is supplied; otherwise render the plain text (start === end
              // ⇒ no <mark>, byte-identical to the prior <pre>).
              highlightStart={highlightOffset?.start ?? 0}
              highlightEnd={highlightOffset?.end ?? 0}
              className="text-xs p-4 whitespace-pre-wrap font-mono text-foreground"
              testId="extracted-text-content"
            />
          ) : (
            <div className="h-full flex items-center justify-center p-6 text-center">
              <p className="text-xs text-text-secondary max-w-sm">
                No extracted text is stored for this file. It may be an image, a video, or a format the
                extractor couldn't read — download the original to inspect it.
              </p>
            </div>
          )}
        </div>
        {ingestionContributedSections && (
          <div
            className="px-4 py-3 border-t border-border bg-muted/30 space-y-1.5"
            data-testid="extracted-text-sed-ingestion"
          >
            <div className="type-section text-text-secondary">
              SED ingestion
            </div>
            <div className="text-[11px] text-text-secondary">
              Extracted via <code className="font-mono">extractFromDocument()</code>.
              {doc.contentHash && (
                <>
                  {' '}Cached on contentHash{' '}
                  <code className="font-mono text-[10px]">{doc.contentHash.slice(0, 12)}…</code>{' '}
                  (KSD6).
                </>
              )}
              {ingestionContributedSections.length > 0 ? (
                <>
                  {' '}Contributed to {ingestionContributedSections.length} SED section
                  {ingestionContributedSections.length === 1 ? '' : 's'}:{' '}
                  <span className="font-mono text-[10px]">
                    {ingestionContributedSections.slice(0, 5).map(s => SED_SECTION_LABELS[s] ?? s).join(', ')}
                    {ingestionContributedSections.length > 5 && `, +${ingestionContributedSections.length - 5} more`}
                  </span>
                  .
                </>
              ) : (
                <> Not yet linked to any SED section — will be on the next Rebuild.</>
              )}
            </div>
            {onReExtract && (
              <button
                type="button"
                onClick={() => void onReExtract(doc)}
                className="text-[10px] text-primary hover:underline underline-offset-2"
                data-testid="extracted-text-re-extract"
              >
                Re-extract this document
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
